import * as MuiIcons from "@mui/icons-material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  useTheme
} from "@mui/material";
import {
  ColDef,
  ICellRendererParams,
  IServerSideDatasource,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHasScope } from "../../../../config/globalConfig";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import { ContextOperation } from "../../context/OperationContext";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import OperationTypes from "../../types";
import TableDynamicActions from "../TableDynamicActions";
import { AutoCompleteOption } from "../common/AutoComplete";
import EmptyStateGrid from "../common/EmptyStateGrid";
import EnumChangeName from "../common/EnumChangeName";
import { nameof } from "../common/ObjectUtils";
import DeleteModal from "./DeleteModal";
import TransferReasonService from "./TransferReasonService";
import { TransferReasonTypes } from "./Types";
import AgGridOperationTable from "../common/AgGridOperationTable";
import FileSaver from "file-saver";
import useToggle from "../../hooks/useToggle";
import ExportTransferRequestsDialog from "./ExportTransferRequestsDialog";

interface Props {
  table: {
    totalElements: number;
    datasource: IServerSideDatasource;
	tenant: string,
    values: any,
    startStationId: string | undefined,
    startStationType: string | undefined,
    expectedEndStationId: string | undefined,
    expectedEndStationType: string | undefined,
    endStationId: string | undefined,
    endStationType: string | undefined,
  };
  updateDatasourceAcriss: () => void;
  submitCount: number;
  setPage: (value: boolean) => void;
  setInitialFormikValue: (value: TransferReasonTypes.AddFunctionTypes) => void;
  startStation: AutoCompleteOption[];
  reason: AutoCompleteOption[];
  tenant: string;
  statusNumber: any[];

}

const TransferReasonTable = (props: Props) => {
  const hasScope = useHasScope;
  const t = useOperationTranslation();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { setLoading, tenant } = useContext(ContextOperation);
  const theme = useTheme();
  const gridTransferRef = useRef<AgGridReact>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [modalData, setModalData] = useState<any>({});
  //const [statusNumber, setStatusNumber] = useState<any>({});
  const enumName = EnumChangeName();
  const [openDownloadModal, setOpenDownloadModal] = useToggle();

  const transferReasonColumns: ColDef[] = [
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("requestDate"),
      headerName: t("transferReason.table.requestDate"),
      minWidth: 230,
      cellRenderer: (params: any) => requestDateColumn(params),
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("reasonCode"),
      headerName: t("transferReason.table.reasonCode"),
      minWidth: 250,
      cellRenderer: (params: any) => reasonCode(params),
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("requestNumber"),
      headerName: t("transferReason.table.requestNumber"),
      minWidth: 200,
      cellRenderer: (params: any) => <span>{params?.data?.number}</span>,
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("state"),
      headerName: t("transferReason.table.state"),
      minWidth: 200,
      cellRenderer: (params: any) => stateColumn(params),
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("deliveryNoteNumber"),
      headerName: t("transferReason.table.deliveryNoteNumber"),
      minWidth: 250,
      cellRenderer: (params: any) => (
        <span>{params?.data?.deliveryNoteNumber}</span>
      ),
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>(
        "startStationDescription"
      ),
      headerName: t("transferReason.table.startStationDescription"),
      minWidth: 200,
      cellRenderer: (params: any) => startStation(params),
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("startDate"),
      headerName: t("transferReason.table.startDate"),
      minWidth: 200,
      cellRenderer: (params: any) => startDateColumn(params),
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>(
        "expectedEndStationDescription"
      ),
      headerName: t("transferReason.table.expectedEndStationDescription"),
      minWidth: 250,
      cellRenderer: (params: any) => expectedEndStation(params),
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("expectedEndDate"),
      headerName: t("transferReason.table.expectedEndDate"),
      minWidth: 250,
      cellRenderer: (params: any) => expectedEndDateColumn(params),
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>(
        "endStationDescription"
      ),
      headerName: t("transferReason.table.endStationDescription"),
      minWidth: 250,
      cellRenderer: (params: any) => endStation(params),
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("endDate"),
      headerName: t("transferReason.table.endDate"),
      minWidth: 250,
      cellRenderer: (params: any) => endDateColumn(params),
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("transferType"),
      headerName: t("transferReason.table.transferType"),
      minWidth: 250,
      cellRenderer: (params: any) => transferType(params),
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("operatorCode"),
      headerName: t("transferReason.table.operatorCode"),
      minWidth: 170,
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("operatorCodeApproval"),
      headerName: t("transferReason.table.operatorCodeApproval"),
      minWidth: 170,
    },
    {
      field: nameof<TransferReasonTypes.TableDataTypes>("operatorCodeRejection"),
      headerName: t("transferReason.table.operatorCodeRejection"),
      minWidth: 170,
    },
    {
      field: "",
      pinned: "right",
      minWidth: 100,
      cellRenderer: (
        params: ICellRendererParams<TransferReasonTypes.TableDataTypes>
      ) => {
        return (
          <TableDynamicActions moreColor={theme.palette.accent.main}>
            <Tooltip
              title={t("transferReason.details.title")}
              placement={"left"}
            >
              <Button
                variant="text"
                size="icon"
                onClick={() => details(params)}
              >
                <DescriptionIcon />
              </Button>
            </Tooltip>

            {params.data?.transferType === "DRIVER" &&
              hasScope("SCOPE_UPDATE_MDTRANSPORTATION_TRANSFER_REQUEST") && (
                <Tooltip
                  title={t("transferReason.table.approve")}
                  placement={"left"}
                >
                  <Button
                    variant="text"
                    size="icon"
                    disabled={
                      params.data?.status === "TO_BE_APPROVED" ? false : true
                    }
                    onClick={() => approve(params)}
                  >
                    <CheckCircleOutlineIcon />
                  </Button>
                </Tooltip>
              )}

            {params.data?.transferType === "CAR_TRANSPORTER" &&
              hasScope("SCOPE_UPDATE_MDTRANSPORTATION_TRANSFER_REQUEST") && (
                <Tooltip
                  title={t("transferReason.details.navigate.tender")}
                  placement={"left"}
                >
                  <Button
                    variant="text"
                    size="icon"
                    disabled={
                      params.data?.status === "TO_BE_APPROVED" ||
                      params.data?.status === "CREATED"
                        ? false
                        : true
                    }
                    onClick={() =>
                      navigate(
                        `/operation/transfer/transferRequests/carriers/${params.data?.id}?tenant=${props?.table?.tenant}`
                      )
                    }
                  >
                    <MuiIcons.Flag />
                  </Button>
                </Tooltip>
              )}

            {hasScope("SCOPE_CREATE_MDTRANSPORTATION_DELIVERY_NOTE") && (
              <Tooltip
                title={t("transferReason.table.createDDT")}
                placement={"left"}
              >
                <Button
                  variant="text"
                  size="icon"
                  disabled={
                    params.data?.status === "APPROVED" &&
                    params?.data?.deliveryNoteNumber === undefined
                      ? false
                      : true
                  }
                  onClick={() => ddtMethod(params)}
                >
                  <AssignmentTurnedInIcon />
                </Button>
              </Tooltip>
            )}
            {hasScope("SCOPE_UPDATE_MDTRANSPORTATION_TRANSFER_REQUEST") && (
              <Tooltip
                title={t("transferReason.table.rejected")}
                placement={"left"}
              >
                <Button
                  variant="text"
                  size="icon"
                  disabled={
                    params.data?.status === "TO_BE_APPROVED" ||
                    params.data?.status === "CREATED" ||
                    params.data?.status === "APPROVED"
                      ? false
                      : true
                  }
                  onClick={() => remove(params)}
                >
                  <HighlightOffIcon />
                </Button>
              </Tooltip>
            )}
          </TableDynamicActions>
        );
      },
    },
  ];

  const approve = (params: any) => {
    setLoading(true);
    TransferReasonService.put
      .approve(props.tenant, params.data.id)
      .then((response: any) => {
        setLoading(false);
        props.updateDatasourceAcriss();
        snackbar.show("success", t("transferReason.messages.approve"));
      })
      .catch((response: any) => {
        setLoading(false);
        snackbar.show("error", t("transferReason.messages.notApprove"));
      });
  };

  const ddtMethod = (params: any) => {
    setLoading(true);
    TransferReasonService.put
      .ddt(props.tenant, params.data.id)
      .then((response) => {
        setLoading(false);
        props.updateDatasourceAcriss();
        snackbar.show("success", t("transferReason.messages.sucessDdt"));
      })
      .catch((response: AxiosError<OperationTypes.AxiosErrorResponse>) => {
        setLoading(false);

        if (response.response?.status === 404) {
          snackbar.show("error", t("transferReason.messages.404ErrorDdt"));
        } else if (response.response?.status === 406) {
          snackbar.show("error", t("transferReason.messages.406ErrorDdt"));
        } else if (response.response?.status === 409) {
          snackbar.show("error", t("transferReason.messages.409ErrorDdt"));
        } else if (
          response.response?.status === 400 &&
          response.response.data?.subCode === 10
        ) {
          snackbar.show(
            "error",
            t("transferReason.messages.400ErrorDdtSubCode10")
          );
        } else if (response.response?.status === 400) {
          snackbar.show("error", t("transferReason.messages.400ErrorDdt"));
        } else {
          snackbar.show("error", t("transferReason.messages.500ErrorDdt"));
        }
      });
  };

  const details = (params: any) => {
    const baseUrl = window.location.href;
    const safeId = encodeURIComponent(params?.data?.id ?? '');
    const tenantParam = encodeURIComponent(props.tenant);
    window.open(`${baseUrl}/details/${safeId}?tenant=${tenantParam}`, "_blank");

  };

  const requestDateColumn = (params: any) => {
    if (params?.data?.requestDate) {
      return (
        <Stack>
          {dayjs(`${params.data.requestDate}`).format("DD/MM/YYYY-HH:mm")}
        </Stack>
      );
    }
  };

  const remove = (params: any) => {
    setLoading(true);
    TransferReasonService.put
      .reject(props.tenant, params?.data?.id)
      .then((response) => {
        setLoading(false);
        props.updateDatasourceAcriss();
        snackbar.show("success", t("transferReason.messages.rejectedSuccess"));
      })
      .catch((response) => {
        setLoading(false);
        snackbar.show("error", t("transferReason.messages.rejectedError"));
      });
  };

  const stateColumn = (params: any) => {
    switch (params?.data?.status) {
      case "REJECTED":
        return (
          <Chip
            label={t("transferReason.autocomplete.rejected")}
            variant="filled"
            color="error"
          />
        );

      case "APPROVED":
        return (
          <Chip
            label={t("transferReason.autocomplete.approved")}
            variant="filled"
            color="success"
          />
        );
      case "TO_BE_APPROVED":
        return (
          <Chip
            label={t("transferReason.autocomplete.toBeApproved")}
            variant="filled"
            color="warning"
          />
        );

      case "CREATED":
        return (
          <Chip
            label={t("transferReason.autocomplete.created")}
            variant="filled"
            color="warning"
          />
        );

      case "APPROVED_WITH_DDT":
        return (
          <Chip
            label={t("transferReason.autocomplete.approvedWithDdt")}
            variant="filled"
            color="success"
          />
        );
      case "TRANSFER_COMPLETED":
        return (
          <Chip
            label={t("transferReason.autocomplete.transferCompleted")}
            variant="filled"
            color="info"
          />
        );
      case "DUPLICATE_CANCELLED_BY_SYSTEM":
        return (
          <Chip
            label={t("transferReason.autocomplete.duplicateCancelledBySystem")}
            variant="filled"
            color="info"
          />
        );
       default:
        break;
    }
  };

  const addTransferReason = () => {
    props.setInitialFormikValue({
      id: "",
      transferReasonCode: "",
      status: null,
      startStationId: "",
      startDate: null,
      expectedEndStationId: "",
      expectedEndDate: null,
      endStationId: "",
      endDate: null,
      note: "",
      operatorCode: "",
      companyName: "",
      companyCity: "",
      plates: [],
      driversId: [],
      transferType: "",
      shopRepairLms: ""
    });
    props.setPage(true);
  };

  // const editTransferReason = (data: TransferReasonTypes.EditFunctionTypes) => {
  //   TransferReasonService.get.transferById(data.id).then((response) => {
  //     props.setInitialFormikValue({
  //       id: response.id,
  //       status: "",
  //       transferReasonCode: response.reasonCode,
  //       startStationId: response.startStationId,
  //       startDate: dayjs.utc(response.startDate).format("YYYY-MM-DDTHH:mm:ssZ"),
  //       expectedEndStationId: response.expectedEndStationId,
  //       expectedEndDate: dayjs
  //         .utc(response.expectedEndDate)
  //         .format("YYYY-MM-DDTHH:mm:ssZ"),
  //       endStationId: null,
  //       endDate: null,
  //       note: null,
  //       operatorCode: null,
  //       companyName: "",
  //       companyCity: "",
  //       transferCompanyName: "",
  //       transferCompanyAddress: "",
  //       plates: [],
  //       driversId: driverIdMethod(response?.deliveryNote),
  //     });
  //     props.setPage(true);
  //   });
  // };

  // const driverIdMethod = (deliveryNote: any) => {
  //   const array: any = [];
  //   deliveryNote?.drivers?.map((element: any) => {
  //     array.push(element.id);
  //   });
  //   return array;
  // };

  // const test = (data?: string) => {
  //   let startId = props?.startStation?.find(
  //     (element: any) => element.value == data
  //   );

  //   if (startId !== undefined) return startId.value;
  //   else return "";
  // };

  const reasonCode = (params: any) => {
    let reasonCodeDescr = props?.reason?.find(
      (element: any) => element.value == params.data.reasonCode
    );
    return (
      <Tooltip title={reasonCodeDescr?.label}>
        <span>{!!reasonCodeDescr?.label ? reasonCodeDescr?.label : ""}</span>
      </Tooltip>
    );
  };

  const startDateColumn = (params: any) => {
    if (params?.data?.startDate) {
      return (
        <Stack>
          {dayjs(`${params.data.startDate}`).format("DD/MM/YYYY-HH:mm")}
        </Stack>
      );
    }
  };

  const expectedEndDateColumn = (params: any) => {
    if (params?.data?.expectedEndDate) {
      return (
        <Stack>
          {dayjs(`${params.data.expectedEndDate}`).format("DD/MM/YYYY-HH:mm")}
        </Stack>
      );
    }
  };

  const endDateColumn = (params: any) => {
    if (params?.data?.endDate) {
      return (
        <Stack>
          {dayjs(`${params.data.endDate}`).format("DD/MM/YYYY-HH:mm")}
        </Stack>
      );
    }
  };

  const startStation = (params: any) => {
    const label = `${params.data.startStationDescription} (${enumName.locationType(params.data.startStationType)})`;
    return (
      <Tooltip title={label}>
        <span>
          {!!label ? label : ""}
        </span>
      </Tooltip>
    );
  };

  const expectedEndStation = (params: any) => {
    const label = `${params.data.expectedEndStationDescription} (${enumName.locationType(params.data.expectedEndStationType)})`;
    return (
      <Tooltip title={label}>
        <span>
          {!!label ? label : ""}
        </span>
      </Tooltip>
    );
  };

  const endStation = (params: any) => {
    const label = `${params.data.endStationDescription} (${enumName.locationType(params.data.endStationType)})`;
    if (!params.data.endStationId) {
      return null;
    }
    return (
      <Tooltip title={label}>
        <span>{!!label ? label : ""}</span>
      </Tooltip>
    );
  };

  const transferType = (params: any) => {
    switch (params.data.transferType) {
      case "DRIVER":
        return t("transferReason.autocomplete.driver");
      case "CAR_TRANSPORTER":
        return t("transferReason.autocomplete.carTransporter");
      default:
        return "";
    }
  };
  
  const handleDownloadExcel = () => {
    setLoading(true);

    const tenant = props.table.tenant;
    const values = props.table.values;
    const startStationId = props.table.startStationId;
    const startStationType = props.table.startStationType;
    const expectedEndStationId = props.table.expectedEndStationId;
    const expectedEndStationType = props.table.expectedEndStationType;
    const endStationId = props.table.endStationId;
    const endStationType = props.table.endStationType;

    TransferReasonService.get
      .exportTransferRequest(
        {
          tenant, 
          ...values,
          startStationId,
          startStationType,
          expectedEndStationId,
          expectedEndStationType,
          endStationId,
          endStationType
      })
      .then((response) => {
        setLoading(false);
        if (response.status === 200) {
          const filename =
          response.headers["content-disposition"].split("filename=")[1];
          var blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          FileSaver.saveAs(blob, filename);
        } else setOpenDownloadModal();

      })
      .catch((error: any) => {
        setLoading(false);

        if(error?.response?.status === 406) {
          snackbar.show("error", t("transferReason.messages.downloadExcelMaxRowsExceeded"));
        } else{
          snackbar.show("error", t("transferReason.messages.downloadErrorMessage"));
        }
      });

      setLoading(false);
	  
};

  //useEffect(() => {
  //  TransferReasonService.get
  //    .statusCounter(tenant)
  //    .then((response) => setStatusNumber(response));
  //}, [props.updateDatasourceAcriss]);

  return (
    <React.Fragment>
      <Box className="carBrandOuterTableBox">
        <Card>
          <Box sx={{ p: 2 }}>
            <Grid container justifyContent="space-between">
              <Grid item>
                <Grid container direction={"row"} spacing={4}>
                  {Object.entries(props.statusNumber).map((el: any) => (
                    <Grid item>
                      <Box component="span" fontWeight={"500"}>
                        {enumName.state(el[0])}
                      </Box>
                      :&nbsp;
                      <Box component="span" fontWeight={"bold"}>
                        {el[1]}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
             <Grid
                width={150}
                container
                spacing={1.5}
                alignItems="center"
                direction="row"
                justifyContent="flex-end"
              >
                <Grid item>
                    <Button
                      variant="text"
                      size="icon"
                      onClick={handleDownloadExcel}
                      color="accent"
                    >
                      <Tooltip title={t("tooltipTitle.download")}>
                        <FileDownloadIcon />
                      </Tooltip>
                    </Button>
                  </Grid>
                <Grid item>
                  {hasScope("SCOPE_CREATE_MDTRANSPORTATION_TRANSFER_REQUEST") && (
                    <Tooltip title={t("tooltipTitle.add")}>
                      <IconButton color="accent" onClick={addTransferReason}>
                        <MuiIcons.Add />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
			  </Grid>
            </Grid>
          </Box>
          <Stack whiteSpace="pre-line">
            <AgGridOperationTable
              gridRef={gridTransferRef}
              columnDefs={transferReasonColumns}
              serverSideDatasource={props.table.datasource}
              serverSideInfiniteScroll={true}
              cacheBlockSize={10}
              rowModelType="serverSide"
              rowStyle={{ width: "300%" }}
              totalElements={props.table.totalElements}
              emptyState={{
                title: t("transferReason.noData.title"),
                content: t("transferReason.noData.body")
              }}
            />

          </Stack>
        </Card>

        {openDeleteModal && (
          <DeleteModal
            setOpenDeleteModal={setOpenDeleteModal}
            openDeleteModal={openDeleteModal}
            modalData={modalData}
            updateDatasourceAcriss={props.updateDatasourceAcriss}
          />
        )}
		
		{openDownloadModal && <ExportTransferRequestsDialog
          setOpenDownloadModal={setOpenDownloadModal}
        />}
		
      </Box>
    </React.Fragment>
  );
};

export default TransferReasonTable;
